import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Hammer, GraduationCap, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const OPTIONS = [
  {
    role: "business",
    icon: Hammer,
    title: "Soy Maestro",
    desc: "Comparte tu oficio: publica talleres, gestiona tus cupos y sesiones, cobra por adelantado y recibe tus liquidaciones.",
    points: ["Crea talleres y agenda sesiones", "Administra alumnos y asistencia", "Cobros seguros y liquidaciones"],
  },
  {
    role: "customer",
    icon: GraduationCap,
    title: "Soy Alumno",
    desc: "Aprende algo nuevo: descubre talleres presenciales, reserva tu cupo en segundos y vive experiencias reales con maestros locales.",
    points: ["Explora talleres por tus intereses", "Reserva y paga de forma segura", "Ticket con código QR de acceso"],
  },
];

export default function RoleChooser() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(null);

  const choose = async (role) => {
    setLoading(role);
    try {
      const { data } = await api.post("/auth/role", { role });
      setUser(data);
      toast.success(role === "business" ? "¡Bienvenido, Maestro!" : "¡Bienvenido, Alumno!");
      navigate(role === "business" ? "/maestro" : "/explorar");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No pudimos guardar tu perfil");
      setLoading(null);
    }
  };

  const firstName = (user?.name || "").split(" ")[0];

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 py-14 bg-[radial-gradient(1200px_600px_at_50%_-10%,hsl(18_55%_92%),transparent)]">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-sm font-medium tracking-wide text-primary uppercase">Un último paso</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold">
            {firstName ? `¡Hola, ${firstName}! ` : "¡Hola! "}¿Cómo quieres usar VIVOTaller?
          </h1>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
            Elige tu perfil para personalizar tu experiencia. Podrás usar la plataforma según el rol que elijas.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {OPTIONS.map((o) => (
            <div
              key={o.role}
              data-testid={`role-card-${o.role}`}
              className="group relative flex flex-col rounded-3xl border border-border bg-card p-7 text-left transition-all hover:border-primary hover:shadow-xl hover:-translate-y-1"
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/10 grid place-items-center text-primary mb-5 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <o.icon className="w-7 h-7" />
              </div>
              <h2 className="font-display text-2xl font-bold">{o.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{o.desc}</p>
              <ul className="mt-5 space-y-2 flex-1">
                {o.points.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
              <Button
                className="mt-6 w-full"
                size="lg"
                disabled={!!loading}
                onClick={() => choose(o.role)}
                data-testid={`choose-${o.role}`}
              >
                {loading === o.role ? <Loader2 className="w-4 h-4 animate-spin" /> : `Continuar como ${o.title.replace("Soy ", "")}`}
              </Button>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          ¿Elegiste mal? Podrás contactarnos para cambiar tu perfil más adelante.
        </p>
      </div>
    </div>
  );
}
